import { SearchTypes } from '../helpers/jobs-query/SearchTypes';
import { CarLicenseEnum } from './carLicense.enum';
import { DispoEmploymentTabIdEnum } from './DispoEmploymentTabId.enum';
import { DepartmentPermissions, DispoPermissions } from './dispoPermissions';
import { InpoolLanguage } from './InpoolLanguage';
import { JobRequestTabId } from './JobRequestTabId.enum';
import { JobTypeEnum } from './jobTypeEnum';
import { LanguageEnum } from './language.enum';
import { NotificationEnum } from './notificationEnum';
import { PoolerEmploymentTabIdEnum } from './PoolerEmploymentTabIdEnum';
import { PoolerJobsTabIdEnum } from './PoolerJobsTabId.enum';
import { RoleEnum } from './role.enum';
import { SexEnum } from './sex.enum';
import { ShiftEnum } from './shift.enum';
import { UserAvailability } from './userAvailability';
import { UploadFile, UserCustomerInformation } from './userCustomerInformation';
import { UserRestWarning } from './userRestWarning';

export type MappedSpeciality = {
  id: string;
  displayName: string;
};
export type MappedCertificate = {
  id: string;
  displayName: string;
};
export type Certificate = {
  id: string;
  displayName: string;
};

export type UserPlaceOfResidence = {
  zip: string;
  location: string;
};

export interface BaseUser extends Meteor.User {
  _id: string;
  role: RoleEnum;
  sex: SexEnum;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  notifyEmail?: boolean;
  notifySms?: boolean;
  pushTokens?: { token: string; arn?: string }[];
  locale?: InpoolLanguage;
  phoneConfirmed?: boolean;
  emailConfirmed?: boolean;
  ccEmails?: string[];
  detailsCollapsed?: boolean;
  twoFactorAuthRequired?: boolean;
  twoFactorAuthEnabled?: boolean;
  services?: {
    splash: any;
    msal?: {
      // current token to verify on resume and log out when expired / revoked
      currentIdToken?: string;
      // msal id from ID-JWT
      id: string;
    };
    adfs?: {
      // current ADFS tokens to verify on resume and log out when expired / revoked
      currentIdToken?: string;
      currentAccessToken?: string;
      // ADFS user id from ID-JWT (usually 'sub' claim)
      id: string;
    };
    password?: any;
    resume?: {
      loginTokens?: {
        when?: Date;
        hashedToken?: string;
      };
    };
  };
  twoFactorAuth?: {
    expiresAt: number;
    code: number;
  };
  isInSetup?: boolean;
  sendEnrollment?: boolean;
  poolRequests?: { customerId: string; displayName: string }[];
  profileImage?: UploadFile;
  isDemo?: boolean;
  isGenerated?: boolean;
  // for demo setup, a user with allowInitialDemoLogin flag can log in before setting a password
  allowInitialDemoLogin?: boolean;
  // isInDemoSetup is used to open the demo setup modal and block the app and removed when the demo setup is complete
  isInDemoSetup?: boolean;
}

export interface PoolerUser extends BaseUser {
  role: RoleEnum.POOL;
  hasJobAssignments?: boolean;
  hasEmployments?: boolean;
  customerInformation?: { [key: string]: UserCustomerInformation };
  documentsSubmitted?: boolean;
  height?: number;
  placeOfResidence?: UserPlaceOfResidence;
  dateOfBirth?: string; // yyyy-MM-dd
  languages?: LanguageEnum[];
  noteReferences?: string[];
  degrees?: string[];
  car?: CarLicenseEnum;
  itKnowledge?: string;
  clothingSize?: string;
  shifts?: ShiftEnum[];
  jobTypes?: JobTypeEnum[];
  userPreferenceDepartmentBlacklist?: string[];
  certificates?: MappedCertificate[];
  specialities?: MappedSpeciality[];
  availabilities: UserAvailability[];
  employmentAvailabilities: EmploymentAvailability[];
  employmentAvailabilityDays: EmploymentAvailabilityDay[];
  employmentDefaultFilters?: {
    [key in PoolerEmploymentTabIdEnum]?: EmploymentsFilter;
  };
  defaultFilters?: { [key in PoolerJobsTabIdEnum]?: JobsFilter };
  restWarnings?: UserRestWarning[];
}

export interface DispoUser extends BaseUser {
  role: RoleEnum.DISPO;
  branch?: string;
  // jobs default filters
  // employment default filters
  employmentDefaultFilters?: {
    [key in DispoEmploymentTabIdEnum]?: EmploymentsFilter;
  };
  getOwnJobConfirmations?: boolean;
  customerId: string;
  dispoPermissions: DispoPermissions;
  isIntemp?: boolean;
  isAdmin?: boolean;
  disabledDispoEmails?: NotificationEnum[];
  defaultFilters?: { [key: string]: JobsFilter };
  jobRequestFilters?: JobRequestFilters;
  transcribeJobListFilters?: TranscribeJobListFilters;
  jobAvailabilitiesOverviewFilter?: JobsAvailabilitiesFilter;
  employmentAvailabilitiesOverviewFilter?: EmploymentsAvailabilitiesFilter;
  recentlyUsed?: {
    specialities?: string[];
    certificates?: string[];
  };
  disableUnsavedChangesWarning?: boolean;
}

export interface RequesterUser extends BaseUser {
  role: RoleEnum.REQUESTER;
  branch?: string;
  customerId: string;
  departmentAccess?: DepartmentPermissions;
  jobRequestFilters?: JobRequestFilters;
}

export type User = PoolerUser | DispoUser | RequesterUser;

export type PoolerUserWithOriginCustomer = PoolerUser & {
  originCustomer?: {
    id: string;
    displayName: string;
  };
};

export type JobsFilter = {
  range: string;
  state: string;
  createdBy?: string;
  branch?: string;
  // shift label like EARLY
  shift?: string;
  departments?: string;
  jobType?: string;
  demandReason?: string;
  certificates?: string;
} & { [key in SearchTypes]?: string | undefined };

export type JobRequestFilter = {
  jobType?: string;
  shift?: string;
  demandReason?: string;
  departmentId?: string;
};

export type JobRequestFilters = {
  [key in JobRequestTabId]?: JobRequestFilter;
};

export interface TranscribeJobListFilter {
  departmentId?: string | null;
  shift?: string | null;
  certificates?: string | null;
  jobType?: string | null;
  range?: string | null;
  searchType?: string | null;
  searchText?: string | null;
  showByTranscribedAtDate?: boolean | null;
}

export type TranscribeJobListFilters = {
  [key: string]: TranscribeJobListFilter;
};

interface JobsAvailabilitiesFilter {
  range?: string;
  search?: string;
  department?: string;
  certificates?: string;
  specialities?: string;
}

interface EmploymentsAvailabilitiesFilter {
  selectedFrom?: string;
  selectedTo?: string;
  department?: string;
  certificates?: string;
  searchText?: string;
}

interface EmploymentsFilter {
  range: string;
  searchText: string;
  departments?: string[] | null;
}

export interface EmploymentAvailability {
  id: string;
  //   yyyy-MM-dd
  startDate: string;
  //   yyyy-MM-dd
  endDate: string;
  status: EmploymentAvailabilityStatusEnum;
  unavailableReason?: UnavailabilityReasonEnum;
  notes?: string;
  workload?: number;
  bookedEmployment?: {
    employmentId: string;
    department: { name: string; id: string };
    organisation: { name: string; id: string };
    inviteeCustomerId: string;
    creatorCustomerId?: string;
    organisationOwnerCustomerIds?: string[];
    unavailabilities?: {
      from: string;
      to: string;
      reason: UnavailabilityReasonEnum;
    }[];
  };
}

export enum UnavailabilityReasonEnum {
  'HOLIDAY' = 'HOLIDAY',
  'SICK' = 'SICK',
  'TRAINING' = 'TRAINING',
  'OTHER' = 'OTHER',
}

export interface EmploymentAvailabilityDay {
  //  yyyy-MM-dd
  date: string;
  status: EmploymentAvailabilityStatusEnum;
  employmentId?: string;
  workload?: number;
  availabilityRanges: EmploymentDayAvailabilityRange[];
}

export type EmploymentDayAvailabilityRange = {
  id: string;
  status: EmploymentAvailabilityStatusEnum;
  employmentId?: string;
  workload?: number;
};

export enum EmploymentAvailabilityStatusEnum {
  'AVAILABLE' = 'AVAILABLE',
  'UNAVAILABLE' = 'UNAVAILABLE',
  'WORKING' = 'WORKING',
}

export interface PasswordResetType {
  token: string;
  password: string;
  type: string;
}

export interface SendEnrollEmailPayload {
  userId: string;
  customerId: string;
  email: string;
}
